import { FC, useState, useEffect, useRef } from 'react';
import { CommonModal } from '../CommonModal';
import { useMasterContext } from '../../context';
import { ImageSlider } from '../ModalItem/ImageSlider';
import { SubscriptionProductViewItem } from '../../utils/subscriptionDetail/types';
//import recommendedSetModalClass from "scss"ファイルから
import recommendedSetModalClass from '../../assets/scss/RecommendedSetModal/RecommendedSetModal.module.scss';
import { ConfirmTitleWithBorder } from '../ModalItem/ConfirmTitleWithBorder';
import { Categories } from '../../api/graphql/master/types';
import { RecProducts } from '../../hooks/use-update-subscription-products';

type RecommendedSetModalProps = {
  active: boolean;
  closeFunc: () => void;
  subscribed: SubscriptionProductViewItem[];
  onBack?: () => void;
  onSelect: (products: RecProducts) => void;
  showBackButton?: boolean;
  onBackClick?: () => void;
};

export const RecommendedSetModal: FC<RecommendedSetModalProps> = ({
  active,
  closeFunc,
  subscribed,
  onBack,
  onSelect,
  showBackButton = false,
  onBackClick = () => console.log('Default back handler'),
}) => {
  const { recommendedSets, categories } = useMasterContext();
  const [selectedSetKey, setSelectedSetKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const [filterRecommendedSets, setFilterRecommendedSets] = useState(recommendedSets ?? {});

  //閉じるとき（Activeじゃなくなるとき）、選択解除
  useEffect(() => {
    if (!active) {
      setSelectedSetKey(null);
    }
  }, [active]);

  useEffect(() => {
    if (subscribed.length === 0) return;
    if (!recommendedSets) return;

    const recommendedSetsByDisplayMatch = {};
    for (const recommendedName in recommendedSets) {
      const recommendedSet = recommendedSets[recommendedName];
      if (!recommendedSet.displayControl) {
        // 表示制御がない場合は無条件で表示
        recommendedSetsByDisplayMatch[recommendedName] = recommendedSet;
      } else {
        // 栄養成分が部分一致している場合は表示
        let isDisplayable = false;
        const includeMatch = includeIngredient(subscribed, recommendedSet.displayControl.include);
        const excludeMatch = includeIngredient(subscribed, recommendedSet.displayControl.exclude);

        if (recommendedSet.displayControl.include) {
          if (includeMatch && !excludeMatch) {
            isDisplayable = true;
          }
        } else if (recommendedSet.displayControl.exclude) {
          if (!excludeMatch) {
            isDisplayable = true;
          }
        }

        if (isDisplayable) {
          recommendedSetsByDisplayMatch[recommendedName] = recommendedSet;
        }
      }
    }

    setFilterRecommendedSets(recommendedSetsByDisplayMatch);
  }, [subscribed, recommendedSets]);

  if (!active || !recommendedSets) return null;

  const handleClose = () => {
    setSelectedSetKey(null);
    closeFunc();
  };

  //セットをどれか選択したとき
  const handleSetSelect = (setKey: string) => {
    setSelectedSetKey(setKey);
    //モーダルタイトルが見えるようにスクロール
    setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  // セット詳細から一覧に戻る
  const handleBack = () => {
    setSelectedSetKey(null);
    //モーダルタイトルが見えるようにスクロール
    setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);

    if (onBack) {
      onBack();
    }
  };

  // セット変更を確定する処理
  const handleConfirm = async () => {
    if (!selectedSetKey || !recommendedSets || !recommendedSets[selectedSetKey]) return;

    setLoading(true);
    try {
      if (!categories || !recommendedSets) throw new Error('Masterデータがありません');
      // 選択されたセットの商品情報を取得して親コンポーネントに渡す
      const select = formattedSelectProducts(recommendedSets[selectedSetKey].products, categories);
      onSelect(select);
    } catch (error) {
      console.error('Failed to update subscription: ', error);
    } finally {
      // closeFunc();
      setLoading(false);
    }
  };

  const content = (
    <div className={recommendedSetModalClass.container}>
      {!selectedSetKey ? (
        <>
          {/* セット一覧表示 */}
          <div ref={titleRef}>
            <ConfirmTitleWithBorder title="別の商品に切り替える" />
            <div className={recommendedSetModalClass.setList}>
              {Object.entries(filterRecommendedSets).map(([key, set]) => (
                <div
                  key={key}
                  className={recommendedSetModalClass.setItem}
                  // onClick={() => handleSetSelect(key)}
                >
                  <div className={recommendedSetModalClass.imageSliderWrapper}>
                    <ImageSlider images={set.productImg.map((url) => ({ url }))}></ImageSlider>
                  </div>
                  <div className={recommendedSetModalClass.setInfo}>
                    {/* セット品名 */}
                    <h3>{key}</h3>
                    {set.subTitle && <p>{set.subTitle}</p>}
                    {set.reason && (
                      <div
                        className={recommendedSetModalClass.reason}
                        dangerouslySetInnerHTML={{
                          __html: set.reason.style
                            ? `<div style="${set.reason.style}">${set.reason.text}</div>`
                            : set.reason.text,
                        }}
                      />
                    )}
                    <div className={recommendedSetModalClass.price}>
                      <div className={recommendedSetModalClass.discountRateWrapper}>
                        <span className={recommendedSetModalClass.discountRate}>
                          {set.discountRate ? set.discountRate : 10}%OFF
                        </span>
                      </div>
                      <span className={recommendedSetModalClass.currentPrice}>
                        ¥{set.price.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className={recommendedSetModalClass.buttonContainer}>
                    <button
                      className={`${recommendedSetModalClass.baseButton} ${recommendedSetModalClass.confirmButton}`}
                      onClick={() => handleSetSelect(key)}
                      disabled={loading}
                    >
                      このセットを選択
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {showBackButton && (
              <div
                className={`${recommendedSetModalClass.buttonContainer} ${recommendedSetModalClass.previousModalButtonContainer}`}
              >
                <button
                  className={`${recommendedSetModalClass.baseButton} ${recommendedSetModalClass.closeButton}`}
                  onClick={onBackClick || (() => console.log('No handler provided'))}
                >
                  もどる
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* 選択したセットの詳細を表示 */}
          <div ref={titleRef}>
            <ConfirmTitleWithBorder title="商品の変更を確認する" />
            <div className={recommendedSetModalClass.setDetail}>
              <div className={recommendedSetModalClass.setDetailFlex}>
                <img
                  src={recommendedSets[selectedSetKey].productImg[0]}
                  alt={selectedSetKey}
                />
                <div className={recommendedSetModalClass.setDetailFlexRight}>
                  <div className={recommendedSetModalClass.setDetailFlexRightColumn}>
                    <h2 className={recommendedSetModalClass.setDetailTitle}>{selectedSetKey}</h2>
                    <div className={recommendedSetModalClass.setDetailPrice}>
                      <div className={recommendedSetModalClass.discountRateWrapper}>
                        <span className={recommendedSetModalClass.discountRate}>
                          {recommendedSets[selectedSetKey].discountRate
                            ? recommendedSets[selectedSetKey].discountRate
                            : 10}
                          %OFF
                        </span>
                      </div>
                      <span className={recommendedSetModalClass.currentPrice}>
                        ¥{recommendedSets[selectedSetKey].price.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={recommendedSetModalClass.detailInfo}>
                {recommendedSets[selectedSetKey].description && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: recommendedSets[selectedSetKey].description.text,
                    }}
                  ></div>
                )}
                {recommendedSets[selectedSetKey].reason && (
                  <div
                    className={recommendedSetModalClass.reason}
                    dangerouslySetInnerHTML={{
                      __html: recommendedSets[selectedSetKey].reason!.text,
                    }}
                  ></div>
                )}
              </div>

              <div className={recommendedSetModalClass.productList}>
                <h3>セット内容</h3>
                <ul>
                  {recommendedSets[selectedSetKey].displayProducts.map((item, index) => (
                    <li
                      key={index}
                      className={recommendedSetModalClass.productItem}
                    >
                      <span className={recommendedSetModalClass.productName}>{item.name}</span>
                      <span className={recommendedSetModalClass.productQuantity}>
                        {item.quantity}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={recommendedSetModalClass.buttonContainer}>
                <button
                  className={`${recommendedSetModalClass.baseButton} ${recommendedSetModalClass.confirmButton}`}
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  このセットに変更する
                </button>
                <button
                  className={recommendedSetModalClass.backButton}
                  onClick={handleBack}
                >
                  他の商品も見る
                </button>
              </div>
              <p className={recommendedSetModalClass.textInfo}>
                変更後もお届け日の3日前まではいつでも商品を変更できます。
              </p>
              <div className={recommendedSetModalClass.divider}></div>
              <div className={recommendedSetModalClass.buttonContainer}>
                <button
                  className={`${recommendedSetModalClass.baseButton} ${recommendedSetModalClass.closeButton}`}
                  onClick={() => {
                    closeFunc();
                    window.parent.postMessage({ action: 'fixedBtnShow' }, '*');
                  }}
                  disabled={loading}
                >
                  今の定期オトク便を続ける
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <CommonModal
      active={active}
      closeFunc={handleClose}
    >
      {content}
    </CommonModal>
  );
};

const includeIngredient = (
  items: SubscriptionProductViewItem[],
  searchString: string | undefined
) => {
  if (!searchString) return false;

  for (const item of items) {
    if (item.type === 'product') {
      const handles = item.product.ingredientPage.split('|');
      const s = handles.some((handle) => handle.includes(searchString));
      if (s) return s;
      continue;
    }

    for (const { ingredientPage, quantity } of item.box.items) {
      if (quantity === 0) continue;
      const handles = ingredientPage.split('|');
      const s = handles.some((handle) => handle.includes(searchString));
      if (s) return s;
    }
  }

  return false;
};

function formattedSelectProducts(products: { [key: string]: number }, categories: Categories) {
  const handleRecord: Record<string, { id: string; planGroupId: string }> = {};

  for (const key in categories) {
    const category = categories[key];

    for (const { handle, productId: id, planGroupId } of category.productList) {
      handleRecord[handle] = {
        id,
        planGroupId,
      };
    }
  }

  const rets: RecProducts = {};
  for (const handle in products) {
    const quantity = products[handle];
    const product = handleRecord[handle];
    if (!product) continue;

    const { id, planGroupId } = product;

    rets[id] = {
      quantity,
      handle,
      planGroupId,
    };
  }

  return rets;
}
